import { BrandingFieldResult } from '../../services/api';

// Display copy for the "Last push result" panel's field_results reasons.
// Previously the panel rendered `{field}: skipped (${reason})` verbatim —
// raw backend reason strings, never the human-readable copy the parent
// spec required (e.g. "Facebook doesn't allow an address on this Page
// type" for `location` + `unsupported`). See
// specs/completed/fb-branding-address-hours-fix.md F3.
//
// Field-specific entries win over the generic ones below. An unknown
// reason never renders blank — it falls back to `${label}: ${reason}` and
// logs a console.warn so an unmapped reason is discoverable during
// development (the backend's own `unmapped field_result reason` WARNING,
// facebook_branding.py, is the durable record — the frontend has no error
// telemetry, see that function's comment).

export type ResultTone = 'ok' | 'skipped' | 'fail';

interface CopyEntry {
    text: string;
    tone: ResultTone;
}

const GENERIC_REASON_COPY: Record<string, (label: string) => string> = {
    no_page_token: (label) => `${label}: no Facebook Page token on file — reconnect Facebook`,
    skipped_no_source: (label) => `${label}: not set in Project Settings — nothing sent`,
    skipped_no_change: (label) => `${label}: already up to date on Facebook`,
    skipped_needs_confirmation: (label) => `${label}: Facebook already has a different value — tick "Replace…" to overwrite it`,
    skipped_unknown_page_state: (label) => `${label}: couldn't read the Page's current value, so nothing was overwritten — try again`,
    skipped_upstream_failure: (label) => `${label}: Facebook connection expired — reconnect Facebook`,
    token_expired: (label) => `${label}: Facebook connection expired — reconnect Facebook`,
    permission_denied: (label) => `${label}: Facebook refused permission — reconnect and grant Page access`,
    missing_scope: (label) => `${label}: Facebook refused permission — reconnect and grant Page access`,
    rate_limited: (label) => `${label}: Facebook rate limit — try again in an hour`,
    network_error: (label) => `${label}: couldn't reach Facebook — try again`,
    graph_error: (label) => `${label}: Facebook rejected this change`,
    invalid_image: (label) => `${label}: image URL couldn't be built for Facebook (server configuration)`,
    fetch_failed: (label) => `${label}: Facebook couldn't fetch the image — try again`,
};

// Field-specific overrides — checked before GENERIC_REASON_COPY.
const FIELD_REASON_COPY: Partial<Record<string, Record<string, string>>> = {
    location: {
        unsupported: 'Address: Facebook doesn\'t allow an address on this Page type',
        skipped_incomplete_address: 'Address: no street line — you picked a suburb or area, not a street address. Facebook needs a street address.',
        skipped_unstructured_address: 'Address: typed rather than picked from suggestions — pick it in Project Settings',
        skipped_address_unconfirmed: 'Address: not confirmed yet — confirm it in Project Settings',
    },
    hours: {
        unsupported: 'Business hours: Facebook doesn\'t allow opening hours on this Page (this can need an address or a local-business Page category)',
        skipped_invalid_hours: 'Business hours: the schedule in Project Settings has an error — fix it there',
        skipped_no_source: 'Business hours: no structured hours (or every day closed) — nothing sent',
    },
};

const warnedReasons = new Set<string>();

export function describeFieldResult(
    field: string,
    label: string,
    result: BrandingFieldResult,
): { text: string; tone: ResultTone; detail: string | null } {
    if (result.ok && !result.reason) {
        return { text: `${label}: updated on Facebook`, tone: 'ok', detail: null };
    }

    const tone: ResultTone = result.skipped ? 'skipped' : 'fail';
    const reason = result.reason || '';

    const fieldSpecific = FIELD_REASON_COPY[field]?.[reason];
    if (fieldSpecific) {
        return { text: fieldSpecific, tone, detail: tone === 'fail' ? result.detail : null };
    }

    const generic = GENERIC_REASON_COPY[reason];
    if (generic) {
        return { text: generic(label), tone, detail: tone === 'fail' ? result.detail : null };
    }

    // Unmapped reason — never blank, but flagged for the developer. The
    // backend's own WARNING (facebook_branding.py, "unmapped field_result
    // reason") is the durable record; this is a dev-time nicety only.
    const warnKey = `${field}:${reason}`;
    if (!warnedReasons.has(warnKey)) {
        warnedReasons.add(warnKey);
        // eslint-disable-next-line no-console
        console.warn('[brandingResultCopy] unmapped reason', field, reason);
    }
    return { text: `${label}: ${reason || 'unknown result'}`, tone, detail: tone === 'fail' ? result.detail : null };
}
